/**
 * Hook for real-time availability checking
 * Provides debounced username and email availability validation
 */

import { useState, useCallback, useRef } from 'react';
import { checkUsernameAvailability, checkEmailAvailability, debounce, AvailabilityStatus } from '../utils/availability.utils';

interface AvailabilityState {
  username: AvailabilityStatus | null;
  email: AvailabilityStatus | null;
}

export const useAvailabilityCheck = () => {
  const [availability, setAvailability] = useState<AvailabilityState>({
    username: null,
    email: null,
  });

  // Use refs to store the latest values to avoid stale closures
  const latestUsernameRef = useRef<string>('');
  const latestEmailRef = useRef<string>('');

  /**
   * Check username availability with debouncing
   */
  const checkUsername = useCallback(
    debounce(async (username: string) => {
      // Only proceed if this is still the latest username
      if (username !== latestUsernameRef.current) {
        return;
      }

      if (!username || username.length < 3) {
        setAvailability(prev => ({ ...prev, username: null }));
        return;
      }

      setAvailability(prev => ({ ...prev, username: 'checking' }));
      
      const status = await checkUsernameAvailability(username);
      
      // Double-check that this is still the current username
      if (username === latestUsernameRef.current) {
        setAvailability(prev => ({ ...prev, username: status }));
      }
    }, 800), // 800ms debounce
    []
  );

  /**
   * Check email availability with debouncing
   */
  const checkEmail = useCallback(
    debounce(async (email: string) => {
      // Only proceed if this is still the latest email
      if (email !== latestEmailRef.current) {
        return;
      }

      if (!email || !email.includes('@')) {
        setAvailability(prev => ({ ...prev, email: null }));
        return;
      }

      setAvailability(prev => ({ ...prev, email: 'checking' }));
      
      const status = await checkEmailAvailability(email);
      
      // Double-check that this is still the current email
      if (email === latestEmailRef.current) {
        setAvailability(prev => ({ ...prev, email: status }));
      }
    }, 800), // 800ms debounce
    []
  );

  /**
   * Trigger username availability check
   */
  const validateUsername = useCallback((username: string) => {
    latestUsernameRef.current = username;
    checkUsername(username);
  }, [checkUsername]);

  /**
   * Trigger email availability check
   */
  const validateEmail = useCallback((email: string) => {
    latestEmailRef.current = email;
    checkEmail(email);
  }, [checkEmail]);

  /**
   * Clear availability status for a field
   */
  const clearAvailability = useCallback((field: 'username' | 'email') => {
    setAvailability(prev => ({ ...prev, [field]: null }));
    
    if (field === 'username') {
      latestUsernameRef.current = '';
    } else {
      latestEmailRef.current = '';
    }
  }, []);

  /**
   * Clear all availability status
   */
  const clearAll = useCallback(() => {
    setAvailability({ username: null, email: null });
    latestUsernameRef.current = '';
    latestEmailRef.current = '';
  }, []);

  return {
    availability,
    validateUsername,
    validateEmail,
    clearAvailability,
    clearAll,
  };
};